import React from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { getTheme } from "../../Theme.js";
const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  padding: 60px;
`;

const Tittle = styled.h1`
  font-size: 4em;
  color: ${theme.MAIN_COLOR};
  text-align: center;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  background-color: ${theme.MAIN_COLOR};
  color: white;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  cursor: pointer;
`;

const NewComponent = () => {
  const history = useHistory();
  return (
    <Main>
      <Tittle>TCS Privacy Policy</Tittle>
      <h1>PRIVACY POLICY</h1>
      <FloatingBackButton onClick={() => history.replace("/")}>
        Back to Home
      </FloatingBackButton>
      <p>Last updated July 16, 2025</p>
      <p>
        This Privacy Notice for Time Card Solutions, LLC (doing business as
        TCS), describes how and why we might access, collect, store, use, and/or
        share ("process") your personal information when you use our services
        ("Services"), including when you:
      </p>
      <ul>
        <li>
          Visit our website at timecardsolutions.com or any website of ours that
          links to this Privacy Notice
        </li>
        <li>
          Download and use our mobile application (TCS Mobile), or any other
          application of ours that links to this Privacy Notice
        </li>
        <li>
          Use TCS, a customizable HR and accounting solution to fit a wide range
          of industry needs
        </li>
        <li>
          Engage with us in other related ways, including any sales, marketing,
          or events
        </li>
      </ul>
      <p>
        Questions or concerns? Reading this Privacy Notice will help you
        understand your privacy rights and choices. We are responsible for
        making decisions about how your personal information is processed. If
        you do not agree with our policies and practices, please do not use our
        Services. If you still have any questions or concerns, please contact us
        at{" "}
        <a href="mailto:support@timecardsolutions.com">
          support@timecardsolutions.com
        </a>
        .
      </p>
      <h2>SUMMARY OF KEY POINTS</h2>
      <ul>
        <li>
          <b>What personal information do we process?</b> When you visit, use,
          or navigate our Services, we may process personal information
          depending on how you interact with us and the Services, the choices
          you make, and the products and features you use.
        </li>
        <li>
          <b>Do we process any sensitive personal information?</b> We do not
          process sensitive personal information.
        </li>
        <li>
          <b>Do we collect any information from third parties?</b> We do not
          collect any information from third parties.
        </li>
        <li>
          <b>How do we process your information?</b> We process your information
          to provide, improve, and administer our Services, communicate with
          you, for security and fraud prevention, and to comply with law. We may
          also process your information for other purposes with your consent.
        </li>
        <li>
          <b>
            In what situations and with which parties do we share personal
            information?
          </b>{" "}
          We may share information in specific situations and with specific
          third parties.
        </li>
        <li>
          <b>How do we keep your information safe?</b> We have adequate
          organizational and technical processes and procedures in place to
          protect your personal information. However, no electronic transmission
          over the internet or information storage technology can be guaranteed
          to be 100% secure.
        </li>
        <li>
          <b>What are your rights?</b> Depending on where you are located
          geographically, the applicable privacy law may mean you have certain
          rights regarding your personal information.
        </li>
        <li>
          <b>How do you exercise your rights?</b> The easiest way to exercise
          your rights is by visiting{" "}
          <a href="https://timecardsolutions.com/user-data">
            timecardsolutions.com/user-data
          </a>
          , or by contacting us. We will consider and act upon any request in
          accordance with applicable data protection laws.
        </li>
      </ul>
      <h2>TABLE OF CONTENTS</h2>
      <ol>
        <li>WHAT INFORMATION DO WE COLLECT?</li>
        <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
        <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
        <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
        <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
        <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
        <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
        <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
        <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
        <li>DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</li>
        <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
        <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
        <li>
          HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
        </li>
      </ol>
      <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
      <h3>Personal information you disclose to us</h3>
      <p>In Short: We collect personal information that you provide to us.</p>
      <p>
        We collect personal information that you voluntarily provide to us when
        you register on the Services, express an interest in obtaining
        information about us or our products and Services, when you participate
        in activities on the Services, or otherwise when you contact us.
      </p>
      <p>
        Personal Information Provided by You. The personal information that we
        collect depends on the context of your interactions with us and the
        Services, the choices you make, and the products and features you use.
        The personal information we collect may include the following:
      </p>
      <ul>
        <li>names</li>
        <li>phone numbers</li>
        <li>email addresses</li>
        <li>mailing addresses</li>
        <li>job titles</li>
        <li>usernames</li>
        <li>passwords</li>
        <li>contact preferences</li>
        <li>billing addresses</li>
        <li>contact or authentication data</li>
        <li>debit/credit card numbers</li>
        <li>gps location</li>
      </ul>
      <p>Sensitive Information. We do not process sensitive information.</p>
      <p>
        Payment Data. We may collect data necessary to process your payment if
        you choose to make purchases, such as your payment instrument number,
        and the security code associated with your payment instrument. All
        payment data is handled and stored by Stripe. You may find their privacy
        notice here:{" "}
        <a href="https://stripe.com/privacy">https://stripe.com/privacy</a>.
      </p>
      <p>
        Application Data. If you use our application(s), we also may collect the
        following information if you choose to provide us with access or
        permission:
      </p>
      <ul>
        <li>Geolocation Information</li>
        <li>Mobile Device Access (camera, storage, and other features)</li>
        <li>
          Mobile Device Data (device ID, model, manufacturer, operating system,
          version, etc.)
        </li>
        <li>Push Notifications</li>
      </ul>
      <p>
        This information is primarily needed to maintain the security and
        operation of our application(s), for troubleshooting, and for our
        internal analytics and reporting purposes.
      </p>
      <p>
        All personal information that you provide to us must be true, complete,
        and accurate, and you must notify us of any changes to such personal
        information.
      </p>
      <h3>Information automatically collected</h3>
      <p>
        In Short: Some information — such as your Internet Protocol (IP) address
        and/or browser and device characteristics — is collected automatically
        when you visit our Services.
      </p>
      <p>
        We automatically collect certain information when you visit, use, or
        navigate the Services. This information does not reveal your specific
        identity (like your name or contact information) but may include device
        and usage information, such as your IP address, browser and device
        characteristics, operating system, language preferences, referring URLs,
        device name, country, location, information about how and when you use
        our Services, and other technical information. This information is
        primarily needed to maintain the security and operation of our Services,
        and for our internal analytics and reporting purposes.
      </p>
      <p>
        Like many businesses, we also collect information through cookies and
        similar technologies.
      </p>
      <p>The information we collect includes:</p>
      <ul>
        <li>Log and Usage Data</li>
        <li>Device Data</li>
        <li>Location Data</li>
      </ul>
      <p>
        Our use of information received from Google APIs will adhere to Google
        API Services User Data Policy, including the Limited Use requirements.
      </p>
      <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
      <p>
        In Short: We process your information to provide, improve, and
        administer our Services, communicate with you, for security and fraud
        prevention, and to comply with law. We may also process your information
        for other purposes with your consent.
      </p>
      <p>
        We process your personal information for a variety of reasons, depending
        on how you interact with our Services, including:
      </p>
      <ul>
        <li>
          To facilitate account creation and authentication and otherwise manage
          user accounts
        </li>
        <li>To respond to user inquiries/offer support to users</li>
        <li>To send administrative information to you</li>
        <li>To enable user-to-user communications</li>
        <li>
          To evaluate and improve our Services, products, marketing, and your
          experience
        </li>
        <li>To identify usage trends</li>
        <li>To comply with our legal obligations</li>
      </ul>
      <h2>3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
      <p>
        In Short: We may share information in specific situations described in
        this section and/or with the following third parties.
      </p>
      <p>
        We may need to share your personal information in the following
        situations:
      </p>
      <ul>
        <li>Business Transfers</li>
        <li>When we use Google Maps Platform APIs</li>
        <li>Affiliates</li>
        <li>Business Partners</li>
        <li>Other Users</li>
      </ul>
      <h2>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
      <p>
        In Short: We may use cookies and other tracking technologies to collect
        and store your information.
      </p>
      <p>
        We may use cookies and similar tracking technologies (like web beacons
        and pixels) to gather information when you interact with our Services.
        Some online tracking technologies help us maintain the security of our
        Services and your account, prevent crashes, fix bugs, save your
        preferences, and assist with basic site functions.
      </p>
      <p>
        We also permit third parties and service providers to use online
        tracking technologies on our Services for analytics and advertising.
      </p>
      <p>
        Specific information about how we use such technologies and how you can
        refuse certain cookies is set out in our Cookie Notice.
      </p>
      <p>
        We may share your information with Google Analytics to track and analyze
        the use of the Services. To opt out of being tracked by Google Analytics
        across the Services, visit{" "}
        <a href="https://tools.google.com/dlpage/gaoptout">
          https://tools.google.com/dlpage/gaoptout
        </a>
        .
      </p>
      <h2>5. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
      <p>
        In Short: We keep your information for as long as necessary to fulfill
        the purposes outlined in this Privacy Notice unless otherwise required
        by law.
      </p>
      <p>
        We will only keep your personal information for as long as it is
        necessary for the purposes set out in this Privacy Notice, unless a
        longer retention period is required or permitted by law.
      </p>
      <p>
        When we have no ongoing legitimate business need to process your
        personal information, we will either delete or anonymize such
        information, or, if this is not possible, then we will securely store
        your personal information and isolate it from any further processing
        until deletion is possible.
      </p>
      <h2>6. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
      <p>
        In Short: We aim to protect your personal information through a system
        of organizational and technical security measures.
      </p>
      <p>
        We have implemented appropriate and reasonable technical and
        organizational security measures designed to protect the security of any
        personal information we process. However, despite our safeguards and
        efforts to secure your information, no electronic transmission over the
        Internet or information storage technology can be guaranteed to be 100%
        secure.
      </p>
      <h2>7. DO WE COLLECT INFORMATION FROM MINORS?</h2>
      <p>
        In Short: We do not knowingly collect data from or market to children
        under 18 years of age.
      </p>
      <p>
        We do not knowingly collect, solicit data from, or market to children
        under 18 years of age, nor do we knowingly sell such personal
        information. If we learn that personal information from users less than
        18 years of age has been collected, we will deactivate the account and
        take reasonable measures to promptly delete such data from our records.
      </p>
      <h2>8. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
      <p>
        In Short: You may review, change, or terminate your account at any time,
        depending on your country, province, or state of residence.
      </p>
      <p>
        Withdrawing your consent: If we are relying on your consent to process
        your personal information, you have the right to withdraw your consent
        at any time.
      </p>
      <p>
        Opting out of marketing and promotional communications: You can
        unsubscribe from our marketing and promotional communications at any
        time by clicking on the unsubscribe link in the emails that we send,
        replying "STOP" or "UNSUBSCRIBE" to the SMS messages that we send, or by
        contacting us.
      </p>
      <p>
        No mobile information will be shared with third parties or affiliates
        for marketing or promotional purposes.
      </p>
      <p>
        Account Information: If you would at any time like to review or change
        the information in your account or terminate your account, you can:
      </p>
      <ul>
        <li>Log in to your account settings and update your user account.</li>
        <li>Contact us using the contact information provided.</li>
      </ul>
      <p>
        Upon your request to terminate your account, we will deactivate or
        delete your account and information from our active databases. However,
        we may retain some information in our files to prevent fraud,
        troubleshoot problems, assist with any investigations, enforce our legal
        terms and/or comply with applicable legal requirements.
      </p>
      <p>
        Cookies and similar technologies: Most Web browsers are set to accept
        cookies by default. If you prefer, you can usually choose to set your
        browser to remove cookies and to reject cookies.
      </p>
      <p>
        If you have questions or comments about your privacy rights, you may
        email us at{" "}
        <a href="mailto:support@timecardsolutions.com">
          support@timecardsolutions.com
        </a>
        .
      </p>
      <h2>9. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
      <p>
        Most web browsers and some mobile operating systems and mobile
        applications include a Do-Not-Track ("DNT") feature or setting you can
        activate to signal your privacy preference not to have data about your
        online browsing activities monitored and collected. At this stage, no
        uniform technology standard for recognizing and implementing DNT signals
        has been finalized. As such, we do not currently respond to DNT browser
        signals or any other mechanism that automatically communicates your
        choice not to be tracked online.
      </p>
      <h2>10. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
      <p>
        If you are a resident of California, Colorado, Connecticut, Delaware,
        Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana,
        Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee,
        Texas, Utah, or Virginia, you may have the right to request access to
        and receive details about the personal information we maintain about you
        and how we have processed it, correct inaccuracies, get a copy of, or
        delete your personal information. You may also have the right to
        withdraw your consent to our processing of your personal information.
        These rights may be limited in some circumstances by applicable law.
      </p>
      <h2>11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
      <p>
        In Short: Yes, we will update this notice as necessary to stay compliant
        with relevant laws.
      </p>
      <p>
        We may update this Privacy Notice from time to time. The updated version
        will be indicated by an updated "Revised" date at the top of this
        Privacy Notice.
      </p>
      <h2>12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
      <p>
        If you have questions or comments about this notice, you may contact us
        by post at:
      </p>
      <address>
        Time Card Solutions, LLC
        <br />
        321 W Winnie Lane
        <br />
        STE 104
        <br />
        Carson City, NV 89703
        <br />
        United States
      </address>
      <h2>
        13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
      </h2>
      <p>
        Based on the applicable laws of your country or state of residence in
        the US, you may have the right to request access to the personal
        information we collect from you, details about how we have processed it,
        correct inaccuracies, or delete your personal information. You may also
        have the right to withdraw your consent to our processing of your
        personal information. These rights may be limited in some circumstances
        by applicable law. To request to review, update, or delete your personal
        information, please visit:{" "}
        <a href="https://timecardsolutions.com/user-data">
          timecardsolutions.com/user-data
        </a>
        .
      </p>
    </Main>
  );
};
export default NewComponent;
